import {
  COURSE_SCOPED_SORTS,
  DEFAULT_ORDER,
  DEFAULT_SORT,
  STUDENT_ACTIVITY_FILTERS,
  STUDENT_LIST_MAX_PAGE,
  STUDENT_SEARCH_MAX_LENGTH,
  STUDENT_SORT_FIELDS,
  STUDENT_STATUS_FILTERS,
  isUuid,
} from '../../api/students360';

/**
 * The Directory's query lives in the URL under the API's own names and values (search, courseId,
 * status, activity, atRisk, sort, order, page), so a link such as /students?activity=inactive7 is
 * the filter itself and the dashboard tiles can point at it. Whatever the URL carries is checked
 * here: an unknown or invalid value is dropped, never forwarded (the API answers 400 to it).
 */

/** The order a sort starts in: names A to Z, every date or number newest and highest first. */
export function defaultOrderFor(sort) {
  return sort === 'name' ? 'asc' : DEFAULT_ORDER;
}

function normalize(raw = {}) {
  const courseId = isUuid(raw.courseId) ? raw.courseId : '';
  const search = typeof raw.search === 'string' ? raw.search.trim().slice(0, STUDENT_SEARCH_MAX_LENGTH) : '';
  let status = STUDENT_STATUS_FILTERS.includes(raw.status) ? raw.status : '';
  // Students who never enrolled are not in any course: together with a course nobody matches.
  if (courseId && status === 'none') status = '';
  const activity = STUDENT_ACTIVITY_FILTERS.includes(raw.activity) ? raw.activity : '';

  let sort = STUDENT_SORT_FIELDS.includes(raw.sort) ? raw.sort : DEFAULT_SORT;
  let order = raw.order === 'asc' || raw.order === 'desc' ? raw.order : DEFAULT_ORDER;
  // Progress and enrolment date belong to a selected course's enrollment.
  if (!courseId && COURSE_SCOPED_SORTS.includes(sort)) {
    sort = DEFAULT_SORT;
    order = DEFAULT_ORDER;
  }

  // A typed-in page beyond the API's bound would be a 400 the page cannot recover from.
  const page = Number.isInteger(raw.page) && raw.page > 1 ? Math.min(raw.page, STUDENT_LIST_MAX_PAGE) : 1;
  return {
    search, courseId, status, activity, atRisk: raw.atRisk === true, sort, order, page,
  };
}

/** URLSearchParams -> the query the page works with. */
export function parseDirectoryQuery(searchParams) {
  const get = (key) => searchParams.get(key) || '';
  const page = Number(get('page'));
  return normalize({
    search: get('search'),
    courseId: get('courseId'),
    status: get('status'),
    activity: get('activity'),
    atRisk: get('atRisk') === 'true',
    sort: get('sort'),
    order: get('order'),
    page: Number.isInteger(page) ? page : 1,
  });
}

/**
 * The query after a change. Any change that is not a page turn starts again at page 1, and the
 * result is checked like a URL is (a course-scoped sort without a course falls back to last seen).
 */
export function patchDirectoryQuery(query, patch) {
  const next = { ...query, ...patch };
  if (!('page' in patch)) next.page = 1;
  return normalize(next);
}

/** The query -> URLSearchParams, leaving out everything that is at its default. */
export function toSearchParams(query) {
  const params = new URLSearchParams();
  if (query.search) params.set('search', query.search);
  if (query.courseId) params.set('courseId', query.courseId);
  if (query.status) params.set('status', query.status);
  if (query.activity) params.set('activity', query.activity);
  if (query.atRisk) params.set('atRisk', 'true');
  if (query.sort !== DEFAULT_SORT) params.set('sort', query.sort);
  if (query.order !== DEFAULT_ORDER) params.set('order', query.order);
  if (query.page > 1) params.set('page', String(query.page));
  return params;
}

/** How many filters (not sort or page) are set. */
export function activeFilterCount(query) {
  return ['search', 'courseId', 'status', 'activity']
    .filter((key) => Boolean(query[key])).length + (query.atRisk ? 1 : 0);
}
